import type { JSX } from 'react';
import { useMemo } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router';

import { type AdminProject, getMasterProjects, getSubProjects, useGetProjectsQuery } from 'features/projects';

import NotificationCenterEmptyState from './states/NotificationCenterEmptyState';
import NotificationCenterLoadingState from './states/NotificationCenterLoadingState';

interface NotificationCenterLayoutProps {
  masterProjectId: number;
}

export interface NotificationCenterOutletContext {
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
}

const PAGE_META_BY_PATH = {
  activity: {
    title: 'Actividad',
    description: 'Feed unificado de alertas, tareas y preguntas del proyecto.',
  },
  rules: {
    title: 'Reglas de detección',
    description: 'Define las condiciones que disparan alertas automáticas.',
  },
  settings: {
    title: 'Configuración',
    description: 'Elige cómo y a quién avisar cuando el asistente detecta una acción importante.',
  },
  webhooks: {
    title: 'Webhooks',
    description: 'Gestiona los webhooks del proyecto para recibir notificaciones en sistemas externos.',
  },
} as const;

type NotificationCenterPageMeta = (typeof PAGE_META_BY_PATH)[keyof typeof PAGE_META_BY_PATH];

const getPageMeta = (pathname: string): NotificationCenterPageMeta => {
  if (pathname.endsWith('/rules')) {
    return PAGE_META_BY_PATH.rules;
  }

  if (pathname.endsWith('/settings')) {
    return PAGE_META_BY_PATH.settings;
  }

  if (pathname.endsWith('/webhooks')) {
    return PAGE_META_BY_PATH.webhooks;
  }

  return PAGE_META_BY_PATH.activity;
};

export const useNotificationCenterContext = (): NotificationCenterOutletContext =>
  useOutletContext<NotificationCenterOutletContext>();

const NotificationCenterLayout = ({ masterProjectId }: NotificationCenterLayoutProps): JSX.Element => {
  const { pathname } = useLocation();
  const { data: projects = [], isLoading: projectsLoading } = useGetProjectsQuery();
  const pageMeta = getPageMeta(pathname);

  const selectedMaster = useMemo(
    () => getMasterProjects(projects).find((project) => project.id === masterProjectId) ?? null,
    [masterProjectId, projects]
  );

  const subProjectsForConfig = useMemo((): AdminProject[] => {
    if (selectedMaster === null) {
      return [];
    }

    return getSubProjects(projects, selectedMaster.id).map((sub) => ({
      id: sub.id,
      name: sub.name,
      masterProjectId: sub.masterProjectId,
      masterProject: sub.masterProject
        ? { id: sub.masterProject.id, name: sub.masterProject.name }
        : { id: selectedMaster.id, name: selectedMaster.name },
    }));
  }, [projects, selectedMaster]);

  const selectionMissingOrInvalid = !projectsLoading && selectedMaster === null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <header className="shrink-0 rounded-xl">
        <h1 className="m-0 text-2xl font-bold text-text-default font-display">{pageMeta.title}</h1>
        <p className="m-0 mt-1 max-w-3xl text-sm text-text-muted font-body">{pageMeta.description}</p>
      </header>

      {selectionMissingOrInvalid ? <NotificationCenterEmptyState /> : null}

      {projectsLoading && selectedMaster === null ? <NotificationCenterLoadingState /> : null}

      {selectedMaster !== null ? (
        <Outlet context={{ masterProject: selectedMaster, subProjects: subProjectsForConfig }} />
      ) : null}
    </div>
  );
};

export default NotificationCenterLayout;
